import { useId, useState } from 'react';
import { X } from 'lucide-react';
import { t } from '../../lib/i18n';
import { Avatar, Button, SelectField } from '../../components/ui';
import { useGroupMembers, useMemberName, useRepeatGroups } from './api';

function Chip({ id, disabled, onRemove }: { id: string; disabled: boolean; onRemove: () => void }) {
  const q = useMemberName(id);
  const name = q.data?.fullName ?? t('evac.marshals.unknown');
  return (
    <li className="flex items-center gap-2 rounded-full border border-slate-200 bg-white py-1 pl-1 pr-2 text-sm">
      <Avatar name={name} className="h-6 w-6 text-[10px]" />
      <span>{name}</span>
      {!disabled ? (
        <button type="button" aria-label={t('evac.marshals.remove', { name })} className="rounded p-1 text-slate-600 hover:bg-slate-100" onClick={onRemove}>
          <X aria-hidden="true" className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </li>
  );
}

/** Chooses the members who act as marshals: pick a group, search, add. Names of chosen members are read one by one. */
export function MarshalPicker({ ids, onChange, disabled }: { ids: string[]; onChange: (ids: string[]) => void; disabled: boolean }) {
  const sid = useId();
  const groups = useRepeatGroups();
  const [groupId, setGroupId] = useState('');
  const [search, setSearch] = useState('');
  const chosenGroup = groupId || groups.data?.[0]?.id || '';
  const members = useGroupMembers(chosenGroup, search.trim());

  return (
    <fieldset className="space-y-3">
      <legend className="text-sm font-medium text-slate-800">{t('evac.marshals.title')}</legend>
      <p className="text-xs text-slate-600">{t('evac.marshals.hint')}</p>
      {ids.length === 0 ? (
        <p className="text-sm text-slate-600">{t('evac.marshals.none')}</p>
      ) : (
        <ul aria-label={t('evac.marshals.list')} className="flex flex-wrap gap-2">
          {ids.map((id) => (
            <Chip key={id} id={id} disabled={disabled} onRemove={() => onChange(ids.filter((x) => x !== id))} />
          ))}
        </ul>
      )}
      {!disabled ? (
        groups.data && groups.data.length === 0 ? (
          <p className="text-sm text-slate-600">{t('evac.marshals.noGroups')}</p>
        ) : (
          <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <SelectField label={t('evac.marshals.group')} value={chosenGroup} onChange={(e) => setGroupId(e.target.value)}>
                {(groups.data ?? []).map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </SelectField>
              <div>
                <label htmlFor={`${sid}-q`} className="mb-1 block text-sm font-medium text-slate-800">
                  {t('evac.marshals.search')}
                </label>
                <input id={`${sid}-q`} type="search" value={search} onChange={(e) => setSearch(e.target.value)} className="block min-h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm sm:min-h-9" />
              </div>
            </div>
            <ul className="max-h-48 divide-y divide-slate-200 overflow-y-auto rounded-md border border-slate-200 bg-white">
              {(members.data?.items ?? [])
                .filter((m) => !ids.includes(m.id))
                .map((m) => (
                  <li key={m.id} className="flex items-center justify-between gap-2 p-2 text-sm">
                    <span>{m.fullName}</span>
                    <Button aria-label={t('evac.marshals.add', { name: m.fullName })} onClick={() => onChange([...ids, m.id])}>
                      {t('common.add')}
                    </Button>
                  </li>
                ))}
            </ul>
          </div>
        )
      ) : null}
    </fieldset>
  );
}
